class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // HC: one focus setup per open, whichever path below gets there first.
    this.focusTrapped = false;
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // HC: under prefers-reduced-motion, hc-tokens.css zeroes --duration-default, so the
      // drawer transition in component-cart-drawer.css never runs and transitionend never
      // fires. Without this, focus stayed behind the modal and Tab reached the page.
      if (this.hasNoTransition()) this.trapDrawerFocus();
    });

    this.addEventListener(
      'transitionend',
      () => {
        this.trapDrawerFocus(); // HC: shared with the reduced-motion path above
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.focusTrapped = false; // HC: re-arm the focus setup for the next open
    // HC: the opener can be destroyed while the drawer is open, because a cross-sell quick-add
    // button lives inside #CartDrawer and renderContents replaces it. Focusing a detached node is
    // a silent no-op, so fall back to the cart icon, which always survives the swap.
    const opener = this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble');
    removeTrapFocus(opener);
    document.body.classList.remove('overflow-hidden');
  }

  // HC: lifted out of the transitionend callback so the reduced-motion path can reuse it.
  // Guarded twice: once per open, and never after close(), so a late transitionend (the
  // drawer transitions back out too) cannot trap focus in a closed drawer.
  trapDrawerFocus() {
    if (this.focusTrapped || !this.classList.contains('active')) return;
    this.focusTrapped = true;
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  // HC: true when there is no transition left to wait on. An unreadable duration falls
  // through to false, which keeps the stock transitionend path.
  hasNoTransition() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    const duration = getComputedStyle(this).transitionDuration || '';
    return duration.split(',').every((value) => parseFloat(value) === 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // HC: adding a cross-sell happens with the drawer already open, and the swap above replaces
      // every node inside #CartDrawer, including the button that had focus. open() returns early
      // for an active drawer, so its transitionend re-trap never runs and trapFocus keeps its
      // closed-over first and last on detached nodes, letting Tab escape the dialog. Re-run the
      // same trap open() would have run, against the fresh markup. The not-yet-open path below is
      // unchanged.
      if (this.classList.contains('active')) {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner .drawer__close') || this.querySelector('.drawer__inner');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
        return;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
